import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";

import { useTranslation } from "next-i18next";

export type TimeValue = { hour: number | null; minute: number | null };

type TimeInputProps = {
	label?: string;
	value: TimeValue;
	onChange: (next: TimeValue) => void;

	minuteStep?: number; // 1..60, divisor of 60; else fallback to 1
	readOnly?: boolean;
	flat?: boolean; // No border (for use in tables)

	// Validation
	error?: boolean;
	helperText?: string;

	// Focus wiring
	hourInputRef?: React.RefObject<HTMLInputElement | null>;
	minuteInputRef?: React.RefObject<HTMLInputElement | null>;
	nextFocusRef?: React.RefObject<HTMLInputElement | null>; // after minute complete
	prevFocusRef?: React.RefObject<HTMLInputElement | null>; // when hour empty + Backspace
};

function pad2(n: number): string {
	return String(n).padStart(2, "0");
}

function normalizeMinuteStep(step: number | undefined): number {
	const s = Number(step ?? 1);
	if (!Number.isInteger(s) || s < 1 || s > 60) return 1;
	if (60 % s !== 0) return 1;
	return s;
}

function buildAllowedStrings(min: number, max: number): string[] {
	const arr: string[] = [];
	for (let i = min; i <= max; i++) {
		// Add both otions, with leading 0 or without
		if (i < 10) arr.push(String(i));

		arr.push(pad2(i));
	}
	return arr;
}

function buildAllowedMinuteStrings(step: number): string[] {
	const arr: string[] = [];
	for (let m = 0; m < 60; m += step) {
		// Add both otions, with leading 0 or without
		if (m < 10) arr.push(String(m));

		arr.push(pad2(m));
	}
	return arr;
}

function buildAutocompleteMinuteStrings(step: number): string[] {
	const arr: string[] = [];
	for (let m = 0; m < 60; m += step) {
		// Only add without leading zero for autocomplete display
		arr.push(String(m));
	}
	return arr;
}

function isPrefixOfAny(candidate: string, allowed: string[]): boolean {
	if (candidate.length === 0) return true;
	return allowed.some((a) => a.startsWith(candidate));
}

function toNumberOrNull(s: string): number | null {
	if (s.length !== 2) return null;
	const n = Number(s);
	return Number.isFinite(n) ? n : null;
}

function applyDigit(current: string, digit: string, el: HTMLInputElement): string {
	const start = el.selectionStart ?? current.length;
	const end = el.selectionEnd ?? current.length;
	const nextRaw = current.slice(0, start) + digit + current.slice(end);
	return nextRaw.slice(0, 2);
}

function focusSelect(ref?: React.RefObject<HTMLInputElement | null>): void {
	const el = ref?.current;
	if (!el) return;
	el.focus();
	queueMicrotask(() => {
		el.select();
	});
}

function normalizeToAllowed2DigitsOrEmpty(text: string, allowed: string[]): string {
	if (text.length === 0) return "";
	if (text.length === 1) {
		const padded = `0${text}`;
		return allowed.includes(padded) ? padded : "";
	}
	if (text.length === 2) {
		return allowed.includes(text) ? text : "";
	}
	return "";
}

export const TimeInput: React.FC<TimeInputProps> = ({
	label,
	value,
	onChange,
	minuteStep,
	readOnly = false,
	flat = false,
	error = false,
	helperText,
	hourInputRef,
	minuteInputRef,
	nextFocusRef,
	prevFocusRef,
}) => {
	const internalHourRef = React.useRef<HTMLInputElement>(null);
	const internalMinuteRef = React.useRef<HTMLInputElement>(null);

	const hourRef = hourInputRef ?? internalHourRef;
	const minuteRef = minuteInputRef ?? internalMinuteRef;

	const step = React.useMemo(() => normalizeMinuteStep(minuteStep), [minuteStep]);

	const allowedHours = React.useMemo(() => buildAllowedStrings(0, 24), []);
	const allowedMinutes = React.useMemo(() => buildAllowedMinuteStrings(step), [step]);
	const autocompleteMinutes = React.useMemo(() => buildAutocompleteMinuteStrings(step), [step]);

	const [hourText, setHourText] = React.useState(value.hour == null ? "" : pad2(value.hour));
	const [minuteText, setMinuteText] = React.useState(
		value.minute == null ? "" : pad2(value.minute),
	);

	// Track if user is actively editing (field is focused)
	const [isHourFocused, setIsHourFocused] = React.useState(false);
	const [isMinuteFocused, setIsMinuteFocused] = React.useState(false);

	const emit = React.useCallback(
		(hText: string, mText: string) => {
			onChange({ hour: toNumberOrNull(hText), minute: toNumberOrNull(mText) });
		},
		[onChange],
	);

	// Lock minutes to 00 when hour is 24
	const isMinuteLocked = hourText === "24";
	React.useEffect(() => {
		if (isMinuteLocked && minuteText !== "00") {
			setMinuteText("00");
			emit("24", "00");
		}
	}, [isMinuteLocked, minuteText, emit]);

	// sync controlled - only when field is not focused to avoid overwriting user input
	React.useEffect(() => {
		if (isHourFocused) return;
		setHourText(value.hour == null ? "" : pad2(value.hour));
	}, [value.hour, isHourFocused]);
	React.useEffect(() => {
		if (isMinuteFocused) return;
		setMinuteText(value.minute == null ? "" : pad2(value.minute));
	}, [value.minute, isMinuteFocused]);

	const normalizeHour = React.useCallback(() => {
		const normalized = normalizeToAllowed2DigitsOrEmpty(hourText, allowedHours);
		if (normalized !== hourText) setHourText(normalized);
		emit(normalized, minuteText);
	}, [hourText, minuteText, allowedHours, emit]);

	const normalizeMinute = React.useCallback(() => {
		const normalized = normalizeToAllowed2DigitsOrEmpty(minuteText, allowedMinutes);
		if (normalized !== minuteText) setMinuteText(normalized);
		emit(hourText, normalized);
	}, [hourText, minuteText, allowedMinutes, emit]);

	const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
		if (isPrefixOfAny(raw, allowedHours)) {
			setHourText(raw);
			emit(raw, minuteText);
		}
	};

	const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (readOnly) return;
		const { key } = e;
		const el = e.currentTarget;

		// Tab/Shift+Tab: normalize, but don't block default tabbing
		if (key === "Tab") {
			normalizeHour();
			return;
		}

		// Backspace or Delete: allow native behavior for editing
		if (key === "Backspace" || key === "Delete") {
			// Only jump to prevFocusRef if completely empty and backspace
			if (key === "Backspace" && hourText.length === 0 && prevFocusRef) {
				e.preventDefault();
				focusSelect(prevFocusRef);
			}
			return; // Allow native delete/backspace
		}

		if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;

		// digits only
		if (!/^\d$/.test(key)) {
			e.preventDefault();
			return;
		}

		// If field is complete (2 digits), start fresh with new digit
		let next: string;
		if (hourText.length === 2) {
			next = key;
		} else {
			next = applyDigit(hourText, key, el);
		}

		// strict: must stay prefix of a valid hour
		if (!isPrefixOfAny(next, allowedHours)) {
			e.preventDefault();
			return;
		}

		e.preventDefault();
		setHourText(next);
		emit(next, minuteText);

		// auto-advance
		if (next.length === 2) {
			queueMicrotask(() => {
				focusSelect(minuteRef);
			});
		}
	};

	const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
		if (isPrefixOfAny(raw, allowedMinutes)) {
			setMinuteText(raw);
			emit(hourText, raw);
		}
	};

	const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (readOnly) return;
		const { key } = e;
		const el = e.currentTarget;

		if (key === "Tab") {
			normalizeMinute();
			return;
		}

		// Backspace or Delete: allow native behavior for editing
		if (key === "Backspace" || key === "Delete") {
			// Only jump to hour if completely empty and backspace
			if (key === "Backspace" && minuteText.length === 0) {
				e.preventDefault();
				focusSelect(hourRef);
			}
			return; // Allow native delete/backspace
		}

		if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;

		if (!/^\d$/.test(key)) {
			e.preventDefault();
			return;
		}

		// If field is complete (2 digits), start fresh with new digit
		let next: string;
		if (minuteText.length === 2) {
			next = key;
		} else {
			next = applyDigit(minuteText, key, el);
		}

		// strict: must stay prefix of allowed minute strings
		if (!isPrefixOfAny(next, allowedMinutes)) {
			e.preventDefault();
			return;
		}

		// strict: if 2 digits, must be exactly allowed
		if (next.length === 2 && !allowedMinutes.includes(next)) {
			e.preventDefault();
			return;
		}

		e.preventDefault();
		setMinuteText(next);
		emit(hourText, next);

		// auto-advance to next field
		if (next.length === 2 && nextFocusRef) {
			queueMicrotask(() => {
				focusSelect(nextFocusRef);
			});
		}
	};

	const handlePasteHour = (e: React.ClipboardEvent<HTMLInputElement>): void => {
		if (readOnly) return;
		e.preventDefault();

		const raw = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 2);

		if (!isPrefixOfAny(raw, allowedHours)) return;
		if (raw.length === 2 && !allowedHours.includes(raw)) return;

		setHourText(raw);
		emit(raw, minuteText);

		if (raw.length === 2)
			queueMicrotask(() => {
				focusSelect(minuteRef);
			});
	};

	const handlePasteMinute = (e: React.ClipboardEvent<HTMLInputElement>): void => {
		if (readOnly) return;
		e.preventDefault();

		const raw = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 2);

		if (!isPrefixOfAny(raw, allowedMinutes)) return;
		if (raw.length === 2 && !allowedMinutes.includes(raw)) return;

		setMinuteText(raw);
		emit(hourText, raw);

		if (raw.length === 2 && nextFocusRef)
			queueMicrotask(() => {
				focusSelect(nextFocusRef);
			});
	};

	return (
		<Stack gap={0} sx={{ position: "relative" }}>
			{label && !flat ? (
				<Typography
					variant="caption"
					sx={{
						position: "absolute",
						top: -8,
						left: 8,
						px: 0.5,
						backgroundColor: "background.paper",
						fontSize: "0.75rem",
						lineHeight: 1,
						zIndex: 1,
						color: readOnly ? "text.disabled" : error ? "error.main" : "text.secondary",
					}}>
					{label}
				</Typography>
			) : null}

			<Stack
				direction="row"
				alignItems="center"
				sx={{
					width: "fit-content",
					border: flat ? 0 : 1,
					borderColor: flat
						? "transparent"
						: readOnly
							? "action.disabled"
							: error
								? "error.main"
								: "divider",
					borderRadius: flat ? 0 : 1,
					padding: flat ? 0 : "7.5px 8px",
					gap: 0.5,
					backgroundColor: flat ? "transparent" : "background.paper",
					"&:focus-within": flat
						? {}
						: {
								borderColor: error ? "error.main" : "primary.main",
								borderWidth: 2,
								padding: "6.5px 7px", // Compensate for thicker border
							},
				}}>
				<TextField
					inputRef={hourRef}
					value={hourText}
					onChange={handleHourChange}
					onKeyDown={handleHourKeyDown}
					onPaste={handlePasteHour}
					onFocus={() => setIsHourFocused(true)}
					onBlur={() => {
						setIsHourFocused(false);
						normalizeHour();
					}}
					placeholder="HH"
					inputProps={{
						inputMode: "numeric",
						pattern: "[0-9]*",
						maxLength: 2,
						"aria-label": label ? `${label} hour` : "hour",
						readOnly: !!readOnly,
					}}
					sx={{
						width: 40,
						"& .MuiOutlinedInput-notchedOutline": {
							border: "none !important",
						},
						"& .MuiInputBase-input": {
							padding: 0,
							textAlign: "center",
							fontSize: "1rem",
						},
					}}
				/>

				<Typography sx={{ userSelect: "none", opacity: readOnly ? 0.6 : 1 }}>:</Typography>

				{step === 1 ? (
					<TextField
						inputRef={minuteRef}
						value={minuteText}
						onChange={handleMinuteChange}
						onKeyDown={handleMinuteKeyDown}
						onPaste={handlePasteMinute}
						onFocus={() => setIsMinuteFocused(true)}
						onBlur={() => {
							setIsMinuteFocused(false);
							normalizeMinute();
						}}
						placeholder="MM"
						inputProps={{
							inputMode: "numeric",
							pattern: "[0-9]*",
							maxLength: 2,
							"aria-label": label ? `${label} minute` : "minute",
							readOnly: !!readOnly || isMinuteLocked,
						}}
						sx={{
							width: 40,
							"& .MuiOutlinedInput-notchedOutline": {
								border: "none !important",
							},
							"& .MuiInputBase-input": {
								padding: 0,
								textAlign: "center",
								fontSize: "1rem",
							},
						}}
					/>
				) : (
					<Autocomplete
						freeSolo
						options={autocompleteMinutes}
						value={minuteText}
						inputValue={minuteText}
						onInputChange={(_, newValue) => {
							// Only allow numeric input up to 2 digits
							const filtered = newValue.replace(/\D/g, "").slice(0, 2);

							console.log("Autocomplete minute input:", { newValue, filtered });

							// Strict: must be prefix of allowed minute strings
							if (!isPrefixOfAny(filtered, allowedMinutes)) {
								return; // Reject invalid input
							}

							setMinuteText(filtered);
							emit(hourText, filtered);

							// Auto-advance if complete and valid
							if (filtered.length === 2 && allowedMinutes.includes(filtered) && nextFocusRef) {
								queueMicrotask(() => {
									focusSelect(nextFocusRef);
								});
							}
						}}
						onChange={(_, newValue) => {
							if (newValue && typeof newValue === "string") {
								// User selected from dropdown - normalize to 2 digits
								const normalized = pad2(Number(newValue));
								setMinuteText(normalized);
								emit(hourText, normalized);
								if (allowedMinutes.includes(normalized) && nextFocusRef) {
									queueMicrotask(() => {
										focusSelect(nextFocusRef);
									});
								}
							}
						}}
						readOnly={readOnly || isMinuteLocked}
						disableClearable
						openOnFocus
						autoHighlight
						sx={{ width: 50 }}
						slotProps={{
							popper: {
								placement: "bottom-start",
								sx: {
									"& .MuiAutocomplete-listbox": {
										maxHeight: 200,
									},
								},
							},
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								inputRef={minuteRef}
								onKeyDown={(e) => {
									if (readOnly) return;
									const { key } = e;

									if (key === "Tab") {
										normalizeMinute();
										return;
									}

									if (key === "Backspace" && minuteText.length === 0) {
										e.preventDefault();
										focusSelect(hourRef);
										return;
									}

									// Handle Enter to select and advance
									if (key === "Enter" && minuteText.length === 2) {
										e.preventDefault();
										normalizeMinute();
										if (nextFocusRef) {
											queueMicrotask(() => {
												focusSelect(nextFocusRef);
											});
										}
									}
								}}
								onPaste={handlePasteMinute}
								onFocus={() => setIsMinuteFocused(true)}
								onBlur={() => {
									setIsMinuteFocused(false);
									normalizeMinute();
								}}
								placeholder="MM"
								inputProps={{
									...params.inputProps,
									inputMode: "numeric",
									pattern: "[0-9]*",
									maxLength: 2,
									"aria-label": label ? `${label} minute` : "minute",
								}}
								sx={{
									"& .MuiOutlinedInput-root": {
										padding: 0,
									},
									"& .MuiOutlinedInput-notchedOutline": {
										border: "none !important",
									},
									"& .MuiInputBase-input": {
										padding: "0 !important",
										textAlign: "center",
										fontSize: "1rem",
									},
									"& .MuiAutocomplete-endAdornment": {
										display: "none",
									},
								}}
							/>
						)}
					/>
				)}
			</Stack>

			{helperText && (
				<Typography
					variant="caption"
					color={error ? "error" : "text.secondary"}
					sx={{ px: 1, lineHeight: 1.2 }}>
					{helperText}
				</Typography>
			)}
		</Stack>
	);
};

export type TimeRangeValue = { from: TimeValue; to: TimeValue };

type TimeRangeInputProps = {
	value: TimeRangeValue;
	onChange: (next: TimeRangeValue) => void;
	minuteStep?: number;
	readOnly?: boolean;
	flat?: boolean; // No border (for use in tables)
};

export const TimeRangeInput: React.FC<TimeRangeInputProps> = ({
	value,
	onChange,
	minuteStep = 15, // Put TimeTrackingResolion here
	readOnly = false,
	flat = false,
}) => {
	const { t } = useTranslation();

	// Refs for proper chaining across 4 fields
	const fromHourRef = React.useRef<HTMLInputElement>(null);
	const fromMinuteRef = React.useRef<HTMLInputElement>(null);
	const toHourRef = React.useRef<HTMLInputElement>(null);
	const toMinuteRef = React.useRef<HTMLInputElement>(null);

	// Validation: check if from > to
	const isInvalid = React.useMemo(() => {
		const { from, to } = value;
		if (from.hour == null || from.minute == null || to.hour == null || to.minute == null) {
			return false; // Don't validate incomplete times
		}
		const fromMinutes = from.hour * 60 + from.minute;
		const toMinutes = to.hour * 60 + to.minute;
		return fromMinutes > toMinutes;
	}, [value]);

	return (
		<Stack direction="row" gap={1} alignItems="flex-end">
			<TimeInput
				label={t("from")}
				value={value.from}
				onChange={(from) => {
					onChange({ ...value, from });
				}}
				minuteStep={minuteStep}
				readOnly={readOnly}
				flat={flat}
				hourInputRef={fromHourRef}
				minuteInputRef={fromMinuteRef}
				nextFocusRef={toHourRef} // auto-advance from (MM) -> (to HH)
			/>

			<TimeInput
				label={t("to")}
				value={value.to}
				onChange={(to) => {
					onChange({ ...value, to });
				}}
				minuteStep={minuteStep}
				readOnly={readOnly}
				flat={flat}
				error={isInvalid}
				helperText={isInvalid ? "Endzeit muss nach der Startzeit liegen" : undefined}
				hourInputRef={toHourRef}
				minuteInputRef={toMinuteRef}
				prevFocusRef={fromMinuteRef} // backspace on empty (to HH) -> (from MM)
			/>
		</Stack>
	);
};
